/**
 * EventClubAttachmentsPanel
 *
 * Panel reutilizable de Documentos Anexos por club a un evento.
 *
 * Modos:
 *  - canEdit=true: muestra botón de subir + permite borrar archivos propios.
 *    Usado por el director del club.
 *  - canEdit=false: solo lectura (descargar). Usado en la vista consolidada
 *    de la asociación dentro de cada card de club.
 */

import { useMemo, useRef, useState } from "react";
import {
  FileText,
  Upload,
  Trash2,
  Download,
  Plus,
  AlertCircle,
  Loader2,
} from "lucide-react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Badge from "@/components/ui/Badge";
import {
  useEventClubAttachments,
  useUploadEventClubAttachment,
  useDeleteEventClubAttachment,
} from "@/hooks/useEventAttachments";
import {
  ATTACHMENT_TAG_LABELS,
  ATTACHMENT_TAG_OPTIONS,
} from "@/lib/services/eventAttachments";
import { useAuthStore } from "@/stores/authStore";
import type {
  Event,
  EventClubAttachment,
  EventClubAttachmentTag,
} from "@/types";

interface Props {
  event: Pick<Event, "id" | "name">;
  clubId: string;
  clubName: string;
  canEdit: boolean;
  /** Compacto: usar dentro de cards de club en la vista asociación */
  compact?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function tagBadgeColor(tag: EventClubAttachmentTag): string {
  switch (tag) {
    case "contrato_transporte":
    case "contrato_camion":
      return "bg-blue-100 text-blue-700 border border-blue-200";
    case "voto_junta":
    case "carta_junta_iglesia":
      return "bg-purple-100 text-purple-700 border border-purple-200";
    case "permiso_padres":
      return "bg-pink-100 text-pink-700 border border-pink-200";
    case "seguro":
      return "bg-amber-100 text-amber-700 border border-amber-200";
    case "formato_economas":
      return "bg-emerald-100 text-emerald-700 border border-emerald-200";
    default:
      return "bg-gray-100 text-gray-700 border border-gray-200";
  }
}

export function EventClubAttachmentsPanel({
  event,
  clubId,
  clubName,
  canEdit,
  compact = false,
}: Props) {
  const { user } = useAuthStore();
  const { data: items = [], isLoading } = useEventClubAttachments(
    event.id,
    clubId,
  );
  const uploadMutation = useUploadEventClubAttachment();
  const deleteMutation = useDeleteEventClubAttachment();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [tag, setTag] = useState<EventClubAttachmentTag>("contrato_transporte");
  const [customLabel, setCustomLabel] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const grouped = useMemo(() => {
    const map = new Map<EventClubAttachmentTag, EventClubAttachment[]>();
    for (const item of items) {
      const list = map.get(item.tag) || [];
      list.push(item);
      map.set(item.tag, list);
    }
    return map;
  }, [items]);

  const resetForm = () => {
    setTag("contrato_transporte");
    setCustomLabel("");
    setDescription("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = async (selected: File | null) => {
    if (!selected) {
      setFile(null);
      return;
    }
    // Materializar en memoria: en móvil el File es solo una referencia que el
    // SO puede invalidar antes del submit (NotReadableError al subir).
    try {
      const buffer = await selected.arrayBuffer();
      setFile(
        new File([buffer], selected.name, {
          type: selected.type,
          lastModified: selected.lastModified,
        }),
      );
    } catch (err) {
      console.error("No se pudo leer el archivo seleccionado:", err);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    if (tag === "otro" && !customLabel.trim()) {
      return;
    }
    try {
      await uploadMutation.mutateAsync({
        eventId: event.id,
        eventName: event.name,
        clubId,
        clubName,
        tag,
        customLabel: tag === "otro" ? customLabel.trim() : undefined,
        description: description.trim() || undefined,
        file,
        uploadedByName: user?.displayName || "Director",
      });
      resetForm();
      setShowUploadModal(false);
    } catch {
      // toast lo maneja el hook
    }
  };

  const handleDelete = (item: EventClubAttachment) => {
    if (!window.confirm(`¿Eliminar "${item.fileName}"?`)) return;
    deleteMutation.mutate(item);
  };

  const renderItem = (item: EventClubAttachment) => {
    const label =
      item.tag === "otro" && item.customLabel
        ? item.customLabel
        : ATTACHMENT_TAG_LABELS[item.tag];
    const canDelete = canEdit && item.uploadedBy === user?.uid;
    return (
      <div
        key={item.id}
        className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors"
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
          <FileText className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <Badge className={tagBadgeColor(item.tag)}>{label}</Badge>
            <span className="text-xs text-gray-400">
              {formatBytes(item.fileSize)}
            </span>
          </div>
          <p
            className="text-sm font-medium text-gray-900 truncate"
            title={item.fileName}
          >
            {item.fileName}
          </p>
          {item.description && (
            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
              {item.description}
            </p>
          )}
          <p className="text-[11px] text-gray-400 mt-0.5">
            Subido por {item.uploadedByName}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <a
            href={item.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors"
            title="Descargar / abrir"
          >
            <Download className="w-4 h-4" />
          </a>
          {canDelete && (
            <button
              type="button"
              onClick={() => handleDelete(item)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Eliminar"
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={compact ? "" : "space-y-4"}>
      {!compact && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Documentos anexos
            </h3>
            <p className="text-xs text-gray-500">
              Contratos, votos de junta y otros documentos del club para este
              evento.
            </p>
          </div>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Subir documento
            </Button>
          )}
        </div>
      )}

      {compact && canEdit && (
        <div className="flex justify-end mb-2">
          <Button
            variant="outline"
            size="xs"
            onClick={() => setShowUploadModal(true)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Añadir
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          {canEdit
            ? "Aún no has subido documentos para este evento."
            : "Este club no ha subido documentos."}
        </div>
      ) : (
        <div className={compact ? "space-y-2" : "space-y-3"}>
          {Array.from(grouped.entries()).map(([groupTag, list]) => (
            <div key={groupTag} className="space-y-2">
              {!compact && (
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {ATTACHMENT_TAG_LABELS[groupTag]} ({list.length})
                </h4>
              )}
              <div className="space-y-2">{list.map(renderItem)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {canEdit && (
        <Modal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            resetForm();
          }}
          title="Subir documento anexo"
          description={`Para ${event.name} · ${clubName}`}
          size="md"
        >
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Select
              label="Tipo de documento"
              value={tag}
              onChange={(e) => setTag(e.target.value as EventClubAttachmentTag)}
              required
            >
              {ATTACHMENT_TAG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>

            {tag === "otro" && (
              <Input
                label="Nombre del documento"
                placeholder="Ej: Autorización del rector"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                required
              />
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notas sobre este documento..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Archivo <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                required
              />
              {file && (
                <p className="mt-1 text-xs text-gray-500">
                  {file.name} · {formatBytes(file.size)}
                </p>
              )}
              <p className="mt-1 text-[11px] text-gray-400 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                PDF, imagen o Word. Máximo 15 MB.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false);
                  resetForm();
                }}
                disabled={uploadMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={uploadMutation.isPending}
                disabled={
                  !file ||
                  uploadMutation.isPending ||
                  (tag === "otro" && !customLabel.trim())
                }
              >
                <Upload className="w-4 h-4 mr-1" />
                Subir
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default EventClubAttachmentsPanel;
